import {Navigate, useParams, useNavigate} from "react-router-dom";
import React, {useContext, useEffect, useRef, useState} from "react";
import {markDocAsViewedAPI, markSignDocAsCompleted, uploadUserDocumentFileAPI, deleteUserDocumentFileAPI, getSignDocApi} from "../../Services/api";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import {PdfViewer} from "../../Utils/PdfViewer/PdfViewer";
import {get} from "lodash";
import {PageTitle} from "../PageTitle/PageTitle";
import './SignDocPage.scss'
import SignatureCanvas from 'react-signature-canvas';
import {buttonColors} from "../Pickers/InputTheme";
import {Button} from "../Form/Button/Button";
import {MainActionButton} from "../Form/DominantButton/MainActionButton";
import {signDocReadOrWatchMap, SignDocTypes, isSignDocComplete, signDocTypeConfig} from "../SignDoc/SignDocLib";
import {VideoPlayer} from "../../Utils/VideoPlayer/VideoPlayer";
import {Confetti} from "../Smart/Confetti/Confetti";
import {getPageTitleAndSubTitle} from "./SignDocPageLib";
import CanvaEmbed from "../../Utils/Canva/CanvaEmbed";
import {I18nReactContext} from "../../contexts/I18nContext";
import {signDocProgressTypes} from "../SignDoc/SignDocLib";
import {FileUploadBox} from "../Smart/FileUploadBox/FileUploadBox";
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {IconButton} from "@mui/material";
import saveIcon from '../../assets/images/save-full-icon.png'
import {FilePreview, isPreviewableFile} from "../Smart/FilePreview/FilePreview";

export const SignDocPage = () => {
    const {signDocId} = useParams();
    const {i18n} = useContext(I18nReactContext);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [isUploadDocumentLoading, setIsUploadDocumentLoading] = useState(false);
    const [signDoc, setSignDoc] = useState(null);
    const [isReadingCompleted, setIsReadingCompleted] = useState(false);
    const [signatureBase64Temp, setSignatureBase64Temp] = useState(null);
    const [afterServerCompleted, setAfterServerCompleted] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);
    const [requireSignature, setRequireSignature] = useState(false);
    const [fileUploaded, setFileUploaded] = useState([]);
    const [previewFile, setPreviewFile] = useState(null);
    const [resetUploadBox, setResetUploadBox] = useState(false);
    let signatureCanvas = useRef()

    const init = async () => {
        const signDoc = await markDocAsViewedAPI({signDocId});
        if (get(signDoc, 'signDoc.type') === SignDocTypes.UPLOAD_DOCUMENT) {
            setFileUploaded(get(signDoc, 'additionalData.files', []));
        }
        setSignDoc(signDoc);
        setRequireSignature(!signDocProgressTypes.includes(get(signDoc, 'signDoc.type')))
        setIsLoading(false);
    }

    useEffect(() => {
        init()
    }, []);

    useEffect(() => {
        if (showConfetti) {
            setTimeout(async () => {
                let redirectPath = '/learningCenter/myContent';
                try {
                    const mappingJson = window.localStorage.getItem('signDocToLearningPathMap');
                    if (mappingJson) {
                        const mapping = JSON.parse(mappingJson);
                        const learningPathRootId = mapping[signDocId];
                        if (learningPathRootId) {
                            window.localStorage.removeItem('signDocToLearningPathMap');
                            const lpSignDoc = await getSignDocApi({signDocId: learningPathRootId});
                            if (lpSignDoc) {
                                redirectPath = `/learningPathViewer/${learningPathRootId}`;
                            }
                        }
                    }
                } catch (e) {
                    window.localStorage.removeItem('signDocToLearningPathMap');
                }
                navigate(redirectPath, {replace: true});
            }, 5000)
        }
    }, [showConfetti]);

    if (isLoading) {
        return <LoadingSpinner/>
    }
    const onReadingCompleted = async () => {
        setIsReadingCompleted(true)
        if (!requireSignature) {
            await markSignDocAsCompleted({signDocId, signatureBase64: null})
            if (!isCompleted) {
                setShowConfetti(true)
            }
        }
    }

    const onEndDrawing = async () => {
        setSignatureBase64Temp(signatureCanvas.getTrimmedCanvas().toDataURL())
    };

    const onApproval = async () => {
        if (signatureBase64Temp && isReadingCompleted) {
            await markSignDocAsCompleted({signDocId, signatureBase64: signatureBase64Temp})
            setShowConfetti(true)
        }
    }

    const clearCanvas = () => {
        signatureCanvas.clear()
        setSignatureBase64Temp(null)
    }

    const uploadFiles = async ({files}) => {
        // Use different API based on whether this is user completion or admin creation
        setIsUploadDocumentLoading(true);
        let result = await uploadUserDocumentFileAPI({
            files,
            signDocId
        })
        setFileUploaded(result.filesData)
        setIsUploadDocumentLoading(false);
    }


    const handlePreview = (file) => {
        if (isPreviewableFile(file.mimeType) && file.signedUrl) {
            setPreviewFile(file);
        }
    };

    const handleClosePreview = () => {
        setPreviewFile(null);
    };

    const handleSaveAndGoHome = () => {
        setTimeout(() => {
            navigate('/home');
        }, 200);
    };

    const handleDeleteFile = async (fileS3Key) => {
        try {
            await deleteUserDocumentFileAPI({
                signDocId,
                fileS3Key
            });
            // Remove the deleted file from local state
            const updatedFiles = fileUploaded.filter(file => file.s3Key !== fileS3Key);
            setFileUploaded(updatedFiles);
            
            // Reset upload box if no files remain
            if (updatedFiles.length === 0) {
                setResetUploadBox(true);
                // Reset the flag after a brief delay to allow the component to reset
                setTimeout(() => setResetUploadBox(false), 100);
            }
        } catch (error) {
            console.error('Error deleting file:', error);
        }
    };

    if (afterServerCompleted) {
        return <Navigate to={{pathname: '/learningCenter/myContent'}} replace={true}/>
    }
    const signDocType = get(signDoc, 'signDoc.type')
    const doNotForceCompletion = get(signDoc, 'signDoc.doNotForceCompletion')
    const isSigned = get(signDoc, 'signed')
    // Ensure progressPercent is numeric for comparisons
    const progressPercent = Number(get(signDoc, 'progressPercent', 0)) || 0
    const rejectionReason = get(signDoc, 'additionalData.rejectionReason', '')
    const rejectedAt = get(signDoc, 'additionalData.rejectedAt', null)
    const rejectorName = get(signDoc, 'additionalData.rejectorName', '')
    const isRejected = progressPercent === 0 && rejectedAt

    // Use centralized completion logic
    const isCompleted = isSignDocComplete({
        type: signDocType,
        progressPercent,
        viewed: true, // Always true since we're viewing the SignDoc
        signed: isSigned
    });

    const watchOrRead = signDocReadOrWatchMap[signDocType]
    const {title, subTitle} = getPageTitleAndSubTitle({signDoc, i18n})
    const signDocName = get(signDoc, 'signDoc.docName', '')

    return <div className="SignDocPage MenuPage">
        <PageTitle size="minimized"
                   title={title}
                   subTitle={subTitle}
                   subTitleBold={signDocName || ''}
                   customComponent={signDocType === SignDocTypes.UPLOAD_DOCUMENT ? 
                    <MainActionButton
                        title={i18n.translate('signDocPage.uploadDocument.saveButton')}
                        onClick={handleSaveAndGoHome}
                        titlePosition="center"
                        image={saveIcon}
                        disabled={fileUploaded.length === 0}
                        /> : null}
                   noLogo={false}/>
        <div className="menuInnerPage">
            <div className="docAndSignatureWrapper">
                {/* Always use fullWidth for progress-based SignDocs (!requireSignature) since they don't need signatures anymore */}
                <div className={`signDocSectionWrapper ${isCompleted || doNotForceCompletion || !requireSignature ? 'fullWidth' : ''}`}>
                    <div className={`signDocWrapper ${watchOrRead}`}>
                        {signDocType === SignDocTypes.READ_AND_SIGN_DOC_PDF ?
                            <PdfViewer url={get(signDoc, 'signDoc.signedUrl')}
                                       onReadingCompleted={onReadingCompleted}/> : ''}
                        {signDocType === SignDocTypes.WATCH_AND_SIGN_YOUTUBE_LINK ?
                            <div className="videoPlayerContainer">
                                <VideoPlayer videoUrl={get(signDoc, 'signDoc.linkUrl')}
                                             startAtDecimalPercent={get(signDoc, 'progressPercent', 0) / 100}
                                             onWatchComplete={onReadingCompleted}
                                             preventSeeking={!get(signDoc, 'signDoc.allowFastForward', false)}
                                             signDocId={signDocId}/>
                            </div> : ''}
                        {signDocType === SignDocTypes.WATCH_AND_SIGN_VIMEO_LINK ?
                            <div className="videoPlayerContainer">
                                <VideoPlayer videoUrl={get(signDoc, 'signDoc.linkUrl')}
                                             startAtDecimalPercent={get(signDoc, 'progressPercent', 0) / 100}
                                             onWatchComplete={onReadingCompleted}
                                             preventSeeking={!get(signDoc, 'signDoc.allowFastForward', false)}
                                             signDocId={signDocId}/>
                            </div> : ''}
                        {signDocType === SignDocTypes.WATCH_AND_SIGN_VIDEO_FILE ?
                            <div className="videoPlayerContainer">
                                <VideoPlayer videoUrl={get(signDoc, 'signDoc.signedUrl')}
                                             startAtDecimalPercent={get(signDoc, 'progressPercent', 0) / 100}
                                             onWatchComplete={onReadingCompleted}
                                             preventSeeking={!get(signDoc, 'signDoc.allowFastForward', false)}
                                             signDocId={signDocId}/>
                            </div> : ''}
                        {signDocType === SignDocTypes.AUDIO_FILE ?
                            <div className="videoPlayerContainer">
                                <VideoPlayer videoUrl={get(signDoc, 'signDoc.signedUrl')}
                                             startAtDecimalPercent={get(signDoc, 'progressPercent', 0) / 100}
                                             onWatchComplete={onReadingCompleted}
                                             preventSeeking={!get(signDoc, 'signDoc.allowFastForward', false)}
                                             signDocId={signDocId}/>
                            </div> : ''}
                        {signDocType === SignDocTypes.CANVA_PRESENTATION ?
                            <div className="canvaEmbedWrapper">
                                <CanvaEmbed canvaLink={get(signDoc, 'signDoc.linkUrl')}
                                            onReadingCompleted={onReadingCompleted}/>
                            </div> : ''}
                        {signDocType === SignDocTypes.UPLOAD_DOCUMENT ?
                            <div className="uploadDocumentContainer">
                                {isRejected && (
                                    <div className="rejectionNotice">
                                        <div className="rejectionNotice__header">
                                            <WarningAmberIcon className="rejectionNotice__icon" />
                                            {i18n.translate('signDocPage.uploadDocument.rejectionTitle')}
                                        </div>
                                        <div className="rejectionNotice__content">
                                            {rejectionReason ? (
                                                <>
                                                    <div className="rejectionNotice__reason">
                                                        <strong>{i18n.translate('signDocPage.uploadDocument.rejectionReasonLabel')}:</strong> {rejectionReason}
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="rejectionNotice__noReason">
                                                    {i18n.translate('signDocPage.uploadDocument.rejectionNoReason')}
                                                </div>
                                            )}
                                             <div className="rejectionNotice__meta">
                                                {i18n.translate('signDocPage.uploadDocument.rejectedBy')} {rejectorName}
                                            </div>
                                        </div>
                                        <div className="rejectionNotice__action">
                                            {i18n.translate('signDocPage.uploadDocument.rejectionAction')}
                                        </div>
                                    </div>
                                )}
                                <div className="uploadArea">
                                    <div className="uploadInstructions">
                                        <div className="uploadTitle">
                                            {get(signDoc, 'signDoc.description') || i18n.translate('signDocPage.uploadDocument.subtitle')}
                                        </div>
                                    </div>
                                    <FileUploadBox
                                        accept={get(signDocTypeConfig, `[${signDocType}].createEditNewSignDocModal.fileUploadConfig.accept`, {})}
                                        maxSize={get(signDocTypeConfig, `[${signDocType}].createEditNewSignDocModal.fileUploadConfig.maxSize`, 10485760)}
                                        supportedFileTypesForNotifications={get(signDocTypeConfig, `[${signDocType}].createEditNewSignDocModal.fileUploadConfig.supportedFileTypesForNotifications`, [])}
                                        uploadFiles={uploadFiles}
                                        className="uploadDocumentBox"
                                        resetUploadState={resetUploadBox}
                                    >
                                        <div className="uploadDropText">
                                            {fileUploaded.length > 0 ?
                                                i18n.translate('signDocPage.uploadDocument.replaceFile') :
                                                i18n.translate(`newDocSign.modal.dropDocLabel.${signDocType}`)
                                            }
                                        </div>
                                    </FileUploadBox>
                                </div>
                                {isUploadDocumentLoading ? <LoadingSpinner /> : fileUploaded.length > 0 && (
                                    <div className="uploadedFilesArea">
                                        <h3 className="uploadedFilesTitle">{i18n.translate('signDocPage.uploadDocument.uploadedFiles')}</h3>
                                        <ul className="fileList">
                                            {fileUploaded.map((file) => (
                                                <li key={file.s3Key || file.fileName} className="fileListItem">
                                                    <div className="fileIcon">
                                                        <DescriptionIcon />
                                                    </div>
                                                    <div className="fileDetails">
                                                        <div className="fileName">{file.fileName}</div>
                                                        <div className="fileSize">
                                                            {file.fileSize >= 1024 * 1024
                                                                ? `${(file.fileSize / (1024 * 1024)).toFixed(2)} MB`
                                                                : `${(file.fileSize / 1024).toFixed(0)} KB`}
                                                        </div>
                                                    </div>
                                                    <div className="fileStatus">
                                                        {i18n.translate('signDocPage.uploadDocument.waitingApproval')}
                                                    </div>
                                                    <div className="fileActions">
                                                        {isPreviewableFile(file.mimeType) && file.signedUrl && (
                                                            <IconButton onClick={() => handlePreview(file)} className="previewButton" aria-label="preview">
                                                                <VisibilityIcon />
                                                            </IconButton>
                                                        )}
                                                        <IconButton onClick={() => handleDeleteFile(file.s3Key)} className="deleteButton" aria-label="delete">
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div> : ''}
                    </div>
                </div>
                {!isCompleted && !doNotForceCompletion && requireSignature ? <div className="signatureWrapper">
                    <div
                        className="PageSecondaryTitle">{i18n.translate(`signDocPage.signatureTitle.${watchOrRead}`)}</div>
                    <div className="signatureSubtitle">
                        {isReadingCompleted ? i18n.translate(`signDocPage.signatureSubtitle.${watchOrRead}`) : i18n.translate(`signDocPage.signatureSubtitleNotCompleted.${watchOrRead}`)}
                    </div>
                    <div className="signature">
                        {isReadingCompleted ? <SignatureCanvas
                            onEnd={onEndDrawing}
                            ref={(ref) => {
                                signatureCanvas = ref;
                            }}
                            penColor="blue"
                            canvasProps={{
                                className: 'signature-canvas',
                            }}
                        /> : ''}
                    </div>
                    {isReadingCompleted ? <div className="docButtonsWrapper">
                        <Button color={buttonColors.GREY}
                                disabled={!isReadingCompleted || !signatureBase64Temp}
                                onClick={clearCanvas}
                                className="full-width-button"
                                text={i18n.translate('signDocPage.clear')}
                                type="button"
                                component="button"
                                icon={null}
                                actionSucceeded={false}
                                customRef={null}/>
                        <Button color={buttonColors.GOLD}
                                disabled={!isReadingCompleted || !signatureBase64Temp}
                                onClick={onApproval}
                                className="full-width-button"
                                text={i18n.translate(`signDocPage.iApproveReadingTheDoc.${watchOrRead}`)}
                                type="button"
                                component="button"
                                icon={null}
                                actionSucceeded={false}
                                customRef={null}/>

                    </div> : ''}

                </div> : ''}
            </div>
        </div>
        {!doNotForceCompletion ? <Confetti showConfetti={showConfetti}/> : ''}
        <FilePreview
            file={previewFile}
            open={!!previewFile}
            onClose={handleClosePreview}
        />
    </div>
}



